import { cx } from "@emotion/css";
import { isAddress } from "ethers/lib/utils";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import Button from "../../components/Button/Button";
import {
  TextBody,
  TextH2,
  TextSecondary,
} from "../../components/DesignSystem/DesignSystemText";
import Form from "../../components/Form/Form";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import Metadata from "../../components/Metadata/Metadata";
import GenericErrorPage from "../../components/Shared/GenericErrorPage";
import MintingLoader from "../../components/Shared/MintingLoader";
import PageLoader from "../../components/Shared/PageLoader";
import useBackground from "../../hooks/useBackground";
import useClaimKudos from "../../hooks/useClaimKudos";
import { useKudosDetails } from "../../hooks/useKudosDetails";
import useKudosSignature from "../../hooks/useKudosSignature";
import {
  airdrop,
  getIdentityByAddress,
  getUserIdentity,
} from "../../services/apis";
import { useJwtAuth } from "../../services/auth/JwtAuthState";
import { useWallet } from "../../services/wallet/useWallet";
import {
  getEthAddressFromEns,
  isValid0xAddress,
  isValidEnsString,
} from "../../utils/addressUtils";
import { truncateAddress } from "../../utils/string";
import {
  doesUserAlreadyOwnToken,
  isDiscordKudos,
  isUserAllowlisted,
} from "../CreateKudos/utils";
import KudosDetailsCard from "../KudosDetailsCard/KudosDetailsCard";
import ClaimDone from "./ClaimDone";
import { ClaimError, formattedErrorMessage } from "./errors";
import MultiStepAuthModal from "./MultiStepAuthModal";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_SITE_KEY } from "../../config/env";
import SwitchNetworkModal from "../../components/Header/SwitchNetworkModal";

const BackgroundWhiteBluePurple = "/images/BackgroundWhiteBluePurple.png";

enum ClaimState {
  Start = "Start",
  Claiming = "Claiming",
  Minting = "Minting",
  Done = "Done",
  Error = "Error",
}

const Claim = ({ details }: any) => {
  useBackground(BackgroundWhiteBluePurple);
  const router = useRouter();
  const params = router.query;
  const paramsNftId = params?.nftId as string;
  const kudosDetails = useMemo(() => details, [details]);
  const recaptchaRef = useRef<ReCAPTCHA>();

  const {
    fetchKudosDetails,
    loading,
    error: fetchKudosError,
    contributors,
    contributorsResponse,
    fetchContributors,
    ownersResponse,
    owners,
    fetchOwners,
    endorsers,
  } = useKudosDetails(kudosDetails?.tokenId, kudosDetails, true);
  const { username, isCorrectNetwork, addresses, isLoggedIn, discordAccounts } =
    useWallet();
  const { claimKudos } = useClaimKudos();
  const { getKudosSignature } = useKudosSignature();
  const [errorResponse, setError] = useState<ClaimError | null>(null);
  const [state, setState] = useState<ClaimState>(ClaimState.Start);
  const [identity, setIdentity] = useState<any>({});
  const [isAccountAllowlisted, setIsAccountAllowlisted] = useState<
    boolean | null
  >(null);
  const [isTokenAlreadyClaimedByAccount, setIsTokenAlreadyClaimedByAccount] =
    useState<boolean | null>(null);
  const { isAuthenticated } = useJwtAuth();
  const [loadingClaimability, setLoadingClaimability] = useState(false);

  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  useEffect(() => {
    if (isSwitchModalOpen && isCorrectNetwork) {
      setIsSwitchModalOpen(false);
    }
  }, [isCorrectNetwork]);

  const methods = useForm({
    defaultValues: {
      address: "",
    },
    mode: "all",
  });
  const { handleSubmit, setValue, watch } = methods;
  const address = watch("address");

  const setAddress = useCallback(() => {
    if (isLoggedIn && addresses?.length) {
      setValue("address", addresses[0]?.address);
    }
  }, [isLoggedIn, addresses, setValue]);

  useEffect(() => {
    const fetchKudos = async () => {
      await fetchKudosDetails(paramsNftId);
      setAddress();
    };
    fetchKudos();
  }, []);

  const setErrorResponse = (error: ClaimError | null) => {
    recaptchaRef?.current?.reset(); // Resets the reCAPTCHA challenge

    setError(error);

    if (error) {
      setState(ClaimState.Error);
    }
  };

  const isDiscord = useMemo(() => isDiscordKudos(contributors), [contributors]);

  const isSignatureRequired =
    kudosDetails?.claimabilityAttributes?.isSignatureRequired;

  const createdAccountUniqId = kudosDetails?.createdByAddress?.accountUniqId;
  const tokenId = kudosDetails?.tokenId;
  const title = useMemo(() => {
    return isSignatureRequired
      ? `${truncateAddress(createdAccountUniqId)} sent a kudos`
      : "Claim your soulbound Kudos token";
  }, [isSignatureRequired, createdAccountUniqId, tokenId]);

  const subTitle = useMemo(() => {
    return isSignatureRequired
      ? "Sign to claim your Kudos. "
      : "Enter your wallet address or ENS domain to mint. " +
          "Minting is free—no gas.";
  }, [isSignatureRequired, tokenId]);

  const isAllowlistRequired = useMemo(
    () => kudosDetails?.claimabilityAttributes?.isAllowlistRequired,
    [kudosDetails?.claimabilityAttributes?.isAllowlistRequired],
  );
  const expirationTimestamp =
    kudosDetails?.claimabilityAttributes?.expirationTimestamp;
  const isTokenExpired =
    (expirationTimestamp &&
      new Date(expirationTimestamp)?.getTime() < new Date().getTime()) ??
    false;

  const totalClaimCount = kudosDetails?.claimabilityAttributes?.totalClaimCount;
  const remainingClaimCount =
    kudosDetails?.claimabilityAttributes?.remainingClaimCount;
  const noMoreTokens =
    remainingClaimCount === null || totalClaimCount === -1
      ? false
      : remainingClaimCount === 0;

  const isModalOpen = useMemo(() => {
    return !!isSignatureRequired && state === ClaimState.Claiming;
  }, [isSignatureRequired, state]);

  const calculateClaimability = async () => {
    if (!isLoggedIn) {
      setIsAccountAllowlisted(null);
      setIsTokenAlreadyClaimedByAccount(null);
      setErrorResponse(null);
      return;
    }

    try {
      setLoadingClaimability(true);
      setIsAccountAllowlisted(null);
      setIsTokenAlreadyClaimedByAccount(null);
      setErrorResponse(null);

      if (isAllowlistRequired) {
        const isAllowlisted = await isUserAllowlisted(kudosDetails.tokenId, {
          addresses,
          discordAccounts,
        });

        setIsAccountAllowlisted(isAllowlisted);
      }

      const walletAddress = addresses[0]?.address ?? "";
      if (walletAddress) {
        const doesUserOwnToken = await doesUserAlreadyOwnToken(
          kudosDetails.tokenId,
          walletAddress,
        );
        setIsTokenAlreadyClaimedByAccount(doesUserOwnToken);
      }

      setLoadingClaimability(false);
    } catch (error: any) {
      setLoadingClaimability(false);
      toast.error(error.message || error);
    }
  };

  useEffect(() => {
    calculateClaimability();
  }, [isLoggedIn]);

  useEffect(() => {
    setErrorResponse(null);
    setAddress();

    if (!isLoggedIn) {
      setErrorResponse(null);
      return;
    }
    if (isTokenExpired) {
      setErrorResponse(ClaimError.ExpiredToken);
      return;
    }
    if (noMoreTokens) {
      setErrorResponse(ClaimError.NoMoreAvailableTokens);
      return;
    }
    if (isSignatureRequired && isLoggedIn) {
      if (isTokenAlreadyClaimedByAccount) {
        setErrorResponse(ClaimError.ClaimingAddressAlreadyHasToken);
      } else if (isAllowlistRequired && isAccountAllowlisted === false) {
        if (
          (isDiscord && isModalOpen) ||
          (isDiscord && !discordAccounts?.length) ||
          (isDiscord && state === ClaimState.Minting)
        ) {
          // Don't block Discord when modal is open or when discord is not connected.
          // A seperate allowlist check happens in the useDiscord hook in modal context
          return;
        }

        setErrorResponse(ClaimError.NotAllowlistedAsContributor);
      }
    }
  }, [
    isAuthenticated,
    username,
    isTokenExpired,
    noMoreTokens,
    isTokenAlreadyClaimedByAccount,
    isAccountAllowlisted,
    discordAccounts,
    isAllowlistRequired,
    isDiscord,
    isModalOpen,
    discordAccounts?.length,
    isLoggedIn,
    isSignatureRequired,
    setAddress,
    kudosDetails,
    loadingClaimability,
  ]);

  const errorMessage = useMemo(() => {
    return formattedErrorMessage(
      errorResponse,
      address,
      isDiscord && discordAccounts.length > 0
        ? discordAccounts[0].discordDisplayName
        : undefined,
    );
  }, [errorResponse]);

  const onClaimWithSignature = async () => {
    try {
      const types = {
        Claim: [{ name: "tokenId", type: "uint256" }],
      };
      const signature = await getKudosSignature(types, {
        tokenId: Number(kudosDetails.tokenId),
      });

      setState(ClaimState.Minting);
      if (isDiscord) {
        await claimKudos(
          kudosDetails.tokenId,
          signature,
          discordAccounts[0]?.discordUserId,
        );
      } else {
        await claimKudos(kudosDetails.tokenId, signature);
      }
      await onClaimSuccess(username);
    } catch (error) {
      onClaimError(error);
    }
  };

  const submitAirdrop = handleSubmit(async (value) => {
    const captchaToken = await recaptchaRef?.current?.executeAsync();
    if (!captchaToken) {
      setErrorResponse(ClaimError.InvalidCaptcha);
      setState(ClaimState.Error);
      return;
    }

    const tokenId = parseInt(kudosDetails.tokenId);
    const address = value?.address ?? "";

    try {
      setErrorResponse(null);
      setState(ClaimState.Claiming);
      let isValid = isValid0xAddress(address);
      if (isValidEnsString(address)) {
        isValid = !!(await getEthAddressFromEns(address));
      }
      if (!isValid) {
        setErrorResponse(ClaimError.InvalidKudosClaimInput);
        setState(ClaimState.Error);
        return;
      }
      setState(ClaimState.Minting);
      await airdrop(tokenId, {
        address,
        captchaToken,
      });
      await onClaimSuccess(address);
    } catch (error) {
      onClaimError(error);
    }
  });

  const onClaimSuccess = async (usernameOrAddress: string) => {
    let identity;
    try {
      identity = await getUserIdentity(usernameOrAddress);
    } catch (err) {
      // err here means that identity could not be fetched because
      // username does not exist. So we should search by address.
      let ethAddress = usernameOrAddress;
      if (!isAddress(usernameOrAddress)) {
        ethAddress = await getEthAddressFromEns(usernameOrAddress);
      }
      const result: any = await getIdentityByAddress(ethAddress);
      identity = result.data?.length > 0 && result.data[0];
    }
    setIdentity(identity);
    setState(ClaimState.Done);
  };

  const onClaimError = (error: any) => {
    setState(ClaimState.Error);

    if (error.name && error.name in ClaimError) {
      setErrorResponse(error.name);
    } else {
      throw error;
    }
  };

  return (
    <>
      <Metadata
        title={kudosDetails?.headline}
        image={kudosDetails?.generatedImageUrl || kudosDetails?.imageUrl}
        description={kudosDetails?.description}
      />

      {fetchKudosError && <GenericErrorPage errorType="notFound" />}
      {loading && <PageLoader />}
      {!fetchKudosError && !loading && (
        <>
          <MultiStepAuthModal
            open={isModalOpen}
            onClose={() => {
              setState(ClaimState.Start);
            }}
            finalAction={onClaimWithSignature}
            type={isDiscord ? "discord" : "claim"}
            kudosDetails={kudosDetails}
          />

          {state === ClaimState.Minting ? (
            <>
              <MintingLoader shuffle />
            </>
          ) : (
            <div className="my-[20px]">
              {(state === ClaimState.Start ||
                state === ClaimState.Error ||
                state === ClaimState.Claiming) && (
                <>
                  <div className="flex items-center justify-center flex-col">
                    <div className="flex flex-col items-center gap-y-2 mb-11">
                      <TextH2 className="text-center">{title}</TextH2>
                      <TextSecondary className="mb-2 text-center">
                        {subTitle}
                      </TextSecondary>

                      {isSignatureRequired ? (
                        <div className="min-h-[48rem]">
                          {loadingClaimability ? (
                            <LoadingSpinner coloredBg></LoadingSpinner>
                          ) : (
                            <>
                              {errorMessage && (
                                <ErrorMessage>{errorMessage}</ErrorMessage>
                              )}

                              {!errorMessage && (
                                <Button
                                  theme="white"
                                  onClick={() => {
                                    if (!isCorrectNetwork && isLoggedIn) {
                                      setIsSwitchModalOpen(true);
                                    } else {
                                      setState(ClaimState.Claiming);
                                    }
                                  }}
                                  loading={state === ClaimState.Claiming}
                                >
                                  Claim
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <>
                          {/* @ts-ignore */}
                          <Form {...methods} onSubmit={submitAirdrop}>
                            <section className="flex flex-col items-center relative">
                              {!isTokenExpired && !noMoreTokens && (
                                <div className="flex items-center gap-x-4 w-full mb-3">
                                  <Form.Text
                                    name="address"
                                    placeholder="Enter your wallet address or ENS domain"
                                    autoComplete="off"
                                    className="w-[460rem] !h-14"
                                  />
                                  <ReCAPTCHA
                                    ref={
                                      recaptchaRef as React.RefObject<ReCAPTCHA>
                                    }
                                    size="invisible"
                                    sitekey={RECAPTCHA_SITE_KEY}
                                    badge="bottomright"
                                  />
                                  <Button
                                    type="submit"
                                    theme="white"
                                    loading={state === ClaimState.Claiming}
                                    disabled={!address?.length}
                                    className="!h-13 !rounded"
                                  >
                                    Claim
                                  </Button>
                                </div>
                              )}

                              <ErrorMessage className="absolute bottom-[-20rem] whitespace-nowrap">
                                {errorMessage}
                              </ErrorMessage>
                            </section>
                          </Form>
                        </>
                      )}
                    </div>

                    <div className="max-w-[900px] bg-white flex rounded-[32px] shadow-media w-[90%] sm:w-full">
                      <KudosDetailsCard
                        details={kudosDetails}
                        isClaim={true}
                        contributors={contributors}
                        contributorsResponse={contributorsResponse}
                        onShowMoreContributors={async () => {
                          await fetchContributors();
                        }}
                        owners={owners}
                        ownersResponse={ownersResponse}
                        onShowMoreOwners={async () => {
                          await fetchOwners();
                        }}
                        endorsers={endorsers}
                      />
                    </div>
                  </div>
                </>
              )}

              {state === ClaimState.Done && identity && (
                <>
                  <ClaimDone
                    src={kudosDetails?.imageUrl}
                    nftId={parseInt(kudosDetails.tokenId)}
                    username={identity?.username}
                  />
                </>
              )}
            </div>
          )}
        </>
      )}

      <SwitchNetworkModal
        open={isSwitchModalOpen}
        onClose={() => {
          setIsSwitchModalOpen(false);
        }}
      />
    </>
  );
};

const ErrorMessage = ({ children, className }: any) => {
  return (
    <TextBody
      className={cx("!text-[#5D2FBD] !font-bold px-8 text-center", className)}
    >
      {children}
    </TextBody>
  );
};

export default Claim;
